import { ChevronRightIcon } from "lucide-react";
import { useState } from "react";

import type { PolicyProcessingState } from "../../../state/policies";
import { ChunksViewer } from "./ChunksViewer";
import { ProcessingLogs } from "./ProcessingLogs";
import { ProcessingSummary } from "./ProcessingSummary";

/**
 * What the pipeline was asked to do. Fixed for now because it *is* fixed —
 * every policy goes through the same chunk-and-embed path — so this states
 * the contract rather than pretending it is configurable.
 */
function ProcessingSettings({ batchCount }: { batchCount: number }) {
  return (
    <details className="group">
      <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 hover:bg-accent/50">
        <div className="flex items-center gap-2">
          <ChevronRightIcon className="size-4 text-muted-foreground transition-transform group-open:rotate-90" />
          <span className="text-sm font-medium">Processing settings</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-info" />
            Chunking
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-success" />
            Vector storage
          </span>
        </div>
      </summary>
      <div className="border-t border-border/60 bg-muted/30 px-4 py-3 text-sm">
        <p className="text-muted-foreground">
          The document is converted, split into sections and embedded into this organization's
          own vector namespace
          {batchCount > 0 ? `, in ${batchCount} page batch${batchCount === 1 ? "" : "es"}` : ""}.
          Only this organization's members and its agents can retrieve those vectors.
        </p>
      </div>
    </details>
  );
}

/** Page batches as a bar per batch — the shape of the work, at a glance. */
function BatchRail({ processing }: { processing: PolicyProcessingState }) {
  if (processing.batches.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 border-t border-border/60 px-4 py-3">
      {processing.batches.map((batch) => (
        <span
          key={batch.batchNumber}
          title={`Batch ${batch.batchNumber}/${batch.totalBatches} · pages ${batch.startPage}–${batch.endPage} · ${batch.status}`}
          className={
            batch.status === "completed"
              ? "h-1.5 w-6 rounded-full bg-primary"
              : batch.status === "failed"
                ? "h-1.5 w-6 rounded-full bg-destructive"
                : batch.status === "processing"
                  ? "h-1.5 w-6 animate-pulse rounded-full bg-primary/50"
                  : "h-1.5 w-6 rounded-full bg-muted"
          }
        />
      ))}
    </div>
  );
}

/**
 * The run itself: settings, the live console, the run's own tally, and the
 * chunks it produced — one card of stacked sections, in the order someone
 * debugging an ingestion reads them.
 *
 * The progress rail sits at the top and only exists while a run is active; a
 * finished document has a status, not a percentage.
 */
export function ProcessingPanel({
  organizationId,
  policyId,
  processing,
  now,
}: {
  organizationId: string | null;
  policyId: string;
  processing: PolicyProcessingState | null;
  now: number;
}) {
  const [logsOpen, setLogsOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [chunksOpen, setChunksOpen] = useState(false);

  const active = processing?.active ?? false;

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
      {active ? (
        <div className="h-1 w-full bg-muted">
          <div
            className="h-full bg-primary transition-[width] duration-500"
            style={{ width: `${Math.min(100, Math.max(0, processing?.progress ?? 0))}%` }}
          />
        </div>
      ) : null}

      <ProcessingSettings batchCount={processing?.batches.length ?? 0} />
      {processing !== null && active ? <BatchRail processing={processing} /> : null}
      <ProcessingLogs
        processing={processing}
        open={logsOpen}
        onToggle={setLogsOpen}
        now={now}
      />
      <ProcessingSummary
        processing={processing}
        open={summaryOpen}
        onToggle={setSummaryOpen}
      />
      <ChunksViewer
        organizationId={organizationId}
        policyId={policyId}
        open={chunksOpen}
        onToggle={setChunksOpen}
      />
    </div>
  );
}
